import { SortingState, createColumnHelper, flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import 'font-awesome/css/font-awesome.min.css'; // FontAwesome CSS
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import moment from 'moment';
import React, { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { default as Chart, default as ReactApexChart } from 'react-apexcharts';
import { Link, NavigateFunction, useNavigate } from 'react-router-dom';
import StationIssuesCard from '../../components/StationIssue/StationIssuesCard';
import { getAllTickets, getBatteryValue, getDashboardStations, getStation, getTilts, prefetchTimeSeriesData } from '../../services/api.service';

import { useStationMetrics } from '../../hooks/useStationMetrics';
import TiltCanvas, { ITiltPoint } from '../Utils/TiltCanvas';

import { useAuth } from '../../contexts/AuthContext';
import './Home.css';

// Mapping for QC acronyms to human-readable descriptions
const QC_ACRONYM_MAP: Record<string, string> = {
	mc: 'Manual check',
	rc: 'Range climate',
	rs: 'Range sensor',
	td: 'Temporal delta',
	ti: 'Temporal sigma',
	ts: 'Temporal step',
};

// Brief explanations for each QC check (used in the modal for clarity)
const QC_EXPLANATION_MAP: Record<string, string> = {
	mc: 'Manual review performed by an operator',
	rc: 'Value outside the expected climatological range for the location/season',
	rs: 'Value outside the plausible sensor or physical range',
	td: 'Change between consecutive measurements is unusually large',
	ti: 'Value deviates strongly from recent variability (standard deviation check)',
	ts: 'Change of Value between consecutive measurements exceeds limits',
};

// Mapping for QC severity numbers to labels
const QC_SEVERITY_MAP: Record<number, string> = {
	4: 'erroneous',
	3: 'doubtful',
	2: 'inconsistent',
};

type Station = {
	id: string;
	name: string;
	countryCode: string;
	battery: number;
	active: boolean;
	offline24h: boolean;
	offlineWeek: boolean;
	delayed: boolean;
	lastFlag?: string | null;
	lastMeasurement?: string | null;
	[key: string]: any; // to allow extra fields without strict typing
};

type EngineerStationData = {
	id: string;
	name: string;
	lastMeasurement?: string | null;
	lastFlag?: string | null;
	minimumBattery?: number | null;
	maximumBattery?: number | null;
	temperatureMin?: number | null;
	temperatureMax?: number | null;
	windMax?: number | null;
	windAvg?: number | null;
	radiationAvg?: number | null;
	radiationMax?: number | null;
	precipitationSum?: number | null;
	precipitationMax?: number | null;
	precipitationMin?: number | null;
	hasMultiplePrecipitationSensors?: boolean;
	precipitationSensors?: Record<string, {
		sum: number;
		max: number;
		min: number;
	}>;
	pressureMin?: number | null;
	pressureMax?: number | null;
	humidityMin?: number | null;
	humidityMax?: number | null;
	voltageMin?: number | null;
	voltageMax?: number | null;
	flaggedVariables?: string[];
	flaggedVariablesDetail?: Record<
		string,
		{
			count?: number;
			rc?: any;
			rs?: any;
			td?: any;
			ti?: any;
			ts?: any;
			mc?: any;
		}
	>;
	fetchFailed?: boolean;
	failReason?: 'timeout' | 'error' | 'unfinished' | string;
};

type Ticket = {
	_id: string;
	title: string;
	station: string;
	status: string;
	[key: string]: any; // to allow extra fields without strict typing
};

const COUNTRY_NAMES: Record<string, string> = {
	GH: 'Ghana',
	KE: 'Kenya',
	UG: 'Uganda',
	TD: 'Chad',
	NL: 'Netherlands',
	NG: 'Nigeria',
	ZA: 'South Africa',
	RW: 'Rwanda',
	TZ: 'Tanzania',
	SN: 'Senegal',
	ML: 'Mali',
	BE: 'Belgium',
	BF: 'Burkina Faso',
	MG: 'Madagascar',
	ZW: 'Zimbabwe',
	CD: 'Congo',
	ET: 'Ethiopia',
	TG: 'Togo',
	CH: 'Switzerland',
	MW: 'Malawi',
	LS: 'Lesotho',
	MZ: 'Mozambique',
	ZM: 'Zambia',
	CM: 'Cameroon',
	NE: 'Niger',
	CR: 'Costa Rica',
	CI: "Côte d'Ivoire",
	DE: 'Germany',
	BJ: 'Benin',
};

const Home: React.FC = () => {
	const { user } = useAuth();

	// check if current user is an engineer
	const userRole = user?.profile?.role?.toLowerCase();
	const isEngineer = userRole === 'engineer';
	const isNetworkManager = userRole === 'network manager';
	const isCountryManager = userRole === 'country_admin'; // Based on the choices.py file
	const hasRole = userRole && (isEngineer || isNetworkManager || isCountryManager);

	let navigate: NavigateFunction = useNavigate();

	const [stations, setStations] = useState<Station[]>([]);
	const [activeCount, setActiveCount] = useState(0);
	const [flaggedCount, setFlaggedCount] = useState(0);
	const [stationsByCountry, setStationsByCountry] = useState<Record<string, { code: string; count: number }>>({});

	const [stationLoading, setStationLoading] = useState<boolean>(true);
	const [ticketStatusLoading, setTicketStatusLoading] = useState<boolean>(true);
	const [ticketsWithLocation, setTicketsWithLocation] = useState<Ticket[]>([]);

	const [stationActivityData, setStationActivityData] = useState<number[]>([]);

	const [flagError, setFlaggError] = useState<string | null>(null);
	const [ticketError, setTicketError] = useState<string | null>(null);

	const [labelCounts, setLabelCounts] = useState<Record<string, number> | null>(null);
	const [statusCounts, setStatusCounts] = useState<Record<string, number> | null>(null);

	const [lowBattery48h, setLowBattery48h] = useState<Array<{ station: Station; mean: number }>>([]);
	const [lowBatteryLoading, setLowBatteryLoading] = useState<boolean>(true);

	const [tilts, setTilts] = useState<ITiltPoint[]>([]);
	const [tiltsLoading, setTiltsLoading] = useState(false);

	const [initialDataLoaded, setInitialDataLoaded] = useState<boolean>(false);
	const [expandedDataLoading, setExpandedDataLoading] = useState<boolean>(false);

	// Time range state for engineers - initialize from localStorage if available
	const [timeRange, setTimeRange] = useState<number>(() => {
		const saved = localStorage.getItem('homeTimeRange');
		return saved ? parseInt(saved, 10) : 24;
	});

	const columnHelper = createColumnHelper<EngineerStationData>();

	// Use the station metrics hook
	const { data: metricsData, isLoading: isLoadingMetrics, error: metricsError, refetch: refetchMetrics } = useStationMetrics(timeRange);

	// Show loading only if we're loading AND don't have any data yet
	const showLoading = isLoadingMetrics || (metricsData?.loading && (!metricsData?.stations || metricsData.stations.length === 0));

	// Helper to safely extract the stations array from the response
	const engineerStationData: EngineerStationData[] = React.useMemo(() => {
		if (!metricsData) return [];

		// Check if it has stations array
		if (typeof metricsData === 'object' && 'stations' in metricsData && Array.isArray(metricsData.stations)) {
			return metricsData.stations;
		}

		return [];
	}, [metricsData]);

	// Check if backend is still loading
	const isBackendLoading = React.useMemo(() => {
		return metricsData && typeof metricsData === 'object' && 'loading' in metricsData && metricsData.loading === true;
	}, [metricsData]);

	// Combine states for overall loading check
	const engineerDataLoading = isLoadingMetrics || isBackendLoading;
	const hasError = !!metricsError;

	// ADD state for manual refresh
	const [isManualRefreshing, setIsManualRefreshing] = useState(false);

	// Manual refresh handler using React Query's refetch
	const handleManualRefresh = async () => {
		setIsManualRefreshing(true);
		try {
			await refetchMetrics();
			// Wait a bit for the backend task to start and cache to update
			setTimeout(() => {
				setIsManualRefreshing(false);
			}, 2000);
		} catch (error) {
			console.error('Error refreshing data:', error);
			setIsManualRefreshing(false);
		}
	};

	// Prefetch chart data when user hovers a station row (primary variable = 'te', last 7 days)
	const prefetchedStations = useRef<Set<string>>(new Set());
	const handleRowMouseEnter = useCallback((stationId: string) => {
		if (prefetchedStations.current.has(stationId)) return;
		prefetchedStations.current.add(stationId);
		const end = new Date().toISOString();
		const start = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
		// Prefetch the most commonly viewed variables
		prefetchTimeSeriesData(stationId, 'te', start, end, 'auto');
		prefetchTimeSeriesData(stationId, 'lb', start, end, 'auto');
	}, []);

	// Add ref for StationIssuesCard
	const stationIssuesRef = useRef<HTMLDivElement>(null);

	// Table state for engineers
	const [globalFilter, setGlobalFilter] = useState('');
	const [sorting, setSorting] = useState<SortingState>([]);

	// Modal state for station details
	const [isStationModalOpen, setStationModalOpen] = useState<boolean>(false);
	const [selectedStationInfo, setSelectedStationInfo] = useState<{
		id: string;
		name?: string;
		lat?: number | null;
		lng?: number | null;
		countryCode?: string | null;
		lastMeasurement?: string | null;
		flaggedVariables?: string[];
		flaggedVariablesDetail?: Record<string, { count?: number; [k: string]: any }>;
	} | null>(null);
	const [isStationDetailsLoading, setStationDetailsLoading] = useState<boolean>(false);
	const modalMapContainerRef = useRef<HTMLDivElement | null>(null);
	const leafletMapRef = useRef<L.Map | null>(null);
	const leafletMarkerRef = useRef<L.Marker | null>(null);
	const stationMarkerIconRef = useRef<L.DivIcon | null>(null);

	// Variable catalog map: code -> human-readable name
	const [variableNameMap, setVariableNameMap] = useState<Record<string, string>>({});
	// Static mapping of variable codes to names for modal display without extra API requests
	const DEFAULT_VARIABLE_NAME_MAP: Record<string, string> = useMemo(
		() => ({
			lb: 'Battery Level',
			te: 'Temperature',
			ta: 'Tilt angle',
			wg: 'Wind Gust',
			ws: 'Wind Speed',
			wd: 'Wind Direction',
			ra: 'Solar Radiation',
			pr: 'Precipitation',
			rh: 'Humidity',
			ap: 'Air Pressure',
			tx: 'Tilt X',
			ty: 'Tilt Y',
			ld: 'Lightning Distance',
			le: 'Lightning Events',
			se: 'Soil Electrical Conductivity',
			sm: 'Soil Moisture',
			st: 'Soil Temperature',
			ht: 'Temperature of humidity sensor',
		}),
		[]
	);

	// Flagged variables filter state
	const [flagsStartDate, setFlagsStartDate] = useState<string>('');
	const [flagsEndDate, setFlagsEndDate] = useState<string>('');
	const [isFlagsLoading, setIsFlagsLoading] = useState<boolean>(false);

	const closeStationModal = () => {
		setStationModalOpen(false);
		setSelectedStationInfo(null);
		setStationDetailsLoading(false);
		// Clean up map instance
		if (leafletMapRef.current) {
			leafletMapRef.current.remove();
			leafletMapRef.current = null;
		}
		leafletMarkerRef.current = null;
	};

	const ensureLeafletMap = (lat: number, lng: number) => {
		// Initialize or update the map
		if (!modalMapContainerRef.current) return;
		if (!leafletMapRef.current) {
			leafletMapRef.current = L.map(modalMapContainerRef.current).setView([lat, lng], 8);
			L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
				maxZoom: 19,
				attribution: '&copy; OpenStreetMap contributors',
			}).addTo(leafletMapRef.current);
		} else {
			leafletMapRef.current.setView([lat, lng], 8);
		}

		if (leafletMarkerRef.current) {
			leafletMarkerRef.current.setLatLng([lat, lng]);
		} else if (leafletMapRef.current) {
			if (!stationMarkerIconRef.current) {
				stationMarkerIconRef.current = L.divIcon({
					className: 'station-marker-icon',
					html: '<i class="fa fa-map-marker" style="font-size:28px;color:#e11d48;"></i>',
					iconSize: [28, 28],
					iconAnchor: [14, 28],
				});
			}
			leafletMarkerRef.current = L.marker([lat, lng], { icon: stationMarkerIconRef.current }).addTo(leafletMapRef.current);
		}

		// In case the container size changed after opening modal
		setTimeout(() => {
			leafletMapRef.current && leafletMapRef.current.invalidateSize();
		}, 200);
	};

	const openStationModal = async (row: EngineerStationData) => {
		// Initialize modal with data from the row, including flagged variables from batch metrics
		const varsFromRow = Array.isArray(row.flaggedVariables) ? row.flaggedVariables.filter(Boolean) : [];
		const varsDetailFromRow = row.flaggedVariablesDetail || {};
		const rowCountryCode = (row as any)?.countryCode ?? null;
		setSelectedStationInfo({
			id: row.id,
			name: row.name,
			lat: null,
			lng: null,
			countryCode: rowCountryCode,
			lastMeasurement: row.lastMeasurement,
			flaggedVariables: varsFromRow,
			flaggedVariablesDetail: varsDetailFromRow,
		});
		setVariableNameMap(DEFAULT_VARIABLE_NAME_MAP);
		setStationDetailsLoading(true);
		setStationModalOpen(true);

		try {
			// Only fetch station details (for location/name freshness)
			const stationRes = await getStation(row.id);
			const st: any = stationRes.data;
			const lat = st?.latitude ?? st?.lat ?? st?.Latitude ?? st?.Location?.Latitude ?? st?.site?.Latitude ?? null;
			const lng = st?.longitude ?? st?.lon ?? st?.lng ?? st?.Longitude ?? st?.Location?.Longitude ?? st?.site?.Longitude ?? null;
			const name = st?.name ?? row.name;
			const fetchedCountryCode = st?.countryCode ?? st?.country_code ?? st?.country?.code ?? st?.CountryCode ?? st?.Country ?? st?.country ?? null;
			setSelectedStationInfo((prev) =>
				prev
					? {
							...prev,
							lat,
							lng,
							name,
							countryCode: fetchedCountryCode ?? prev.countryCode ?? null,
					  }
					: {
							id: row.id,
							name,
							lat,
							lng,
							countryCode: fetchedCountryCode,
							lastMeasurement: row.lastMeasurement,
							flaggedVariables: varsFromRow,
							flaggedVariablesDetail: varsDetailFromRow,
					  }
			);
			setTiltsLoading(true);
			getTilts(row.id)
				.then((response) => {
					setTilts(response.data);
					setTiltsLoading(false);
				})
				.catch((err) => {
					console.error('Failed to load tilts:', err);
					setTiltsLoading(false);
				});
			// Note: map init happens in useEffect after render
		} catch (e) {
			// Fail silently; modal will show available info
		} finally {
			setStationDetailsLoading(false);
		}
	};

	// Initialize or update map after modal content (map container) is rendered
	useEffect(() => {
		if (isStationModalOpen && !isStationDetailsLoading && selectedStationInfo && selectedStationInfo.lat != null && selectedStationInfo.lng != null) {
			ensureLeafletMap(selectedStationInfo.lat, selectedStationInfo.lng);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isStationModalOpen, isStationDetailsLoading, selectedStationInfo]);

	// Function to scroll to StationIssuesCard
	const scrollToStationIssues = () => {
		stationIssuesRef.current?.scrollIntoView({
			behavior: 'smooth',
			block: 'start',
		});
	};

	// Save time range to localStorage whenever it changes
	useEffect(() => {
		localStorage.setItem('homeTimeRange', timeRange.toString());
	}, [timeRange]);

	useEffect(() => {
		// Variable names now come from backend flagged_variables response per station
		if (!hasRole) return;

		if (isNetworkManager) {
			getDashboardStations().then((res) => {
				const data: Station[] = res.data.stations;

				// Count active
				const activeStations = data.filter((s) => s.active);
				const inactiveStations = data.filter((s) => !s.active);
				setActiveCount(activeStations.length);
				setStationActivityData([activeStations.length, inactiveStations.length]);

				// Count flagged
				const flagged = activeStations.filter((s) => s.lastFlag).length;
				setFlaggedCount(flagged);

				// Group by country name
				const countryDataMap: Record<string, { code: string; count: number }> = {};
				activeStations.forEach((station) => {
					const code = station.countryCode || 'Unknown';
					const countryName = COUNTRY_NAMES[code] || code;

					if (countryDataMap[countryName]) {
						countryDataMap[countryName].count += 1;
					} else {
						countryDataMap[countryName] = { code, count: 1 };
					}
				});

				setStationsByCountry(countryDataMap);

				setStations(data);
				setStationLoading(false);
			});

			const loadTickets = async () => {
				try {
					const tickets = await getAllTickets();

					const labelMap: Record<string, number> = {};
					const statusMap: Record<string, number> = {
						open: 0,
						pending: 0,
						closed: 0,
					};

					const processedTickets: Ticket[] = [];

					tickets.forEach((ticket: Ticket) => {
						// Group by status
						let status = (ticket.status || '').toLowerCase();

						if (status === 'close') {
							status = 'closed';
						}

						if (statusMap.hasOwnProperty(status)) {
							statusMap[status] += 1;
						} else {
							statusMap[status] = 1; // fallback for other statuses
						}

						// Group by labels
						const labels = ticket.labels && ticket.labels.length > 0 ? ticket.labels : ['Other issues'];
						labels.forEach((label: string) => {
							const labelKey = label === 'None' ? 'Other issues' : label;
							labelMap[labelKey] = (labelMap[labelKey] || 0) + 1;
						});

						// Add lat/lng for mapping if available
						const lat = ticket.site?.Latitude;
						const lng = ticket.site?.Longitude;

						processedTickets.push({
							...ticket,
							status,
							lat,
							lng,
						});
					});

					setLabelCounts(labelMap);
					setStatusCounts(statusMap);
					setTicketsWithLocation(processedTickets); // <-- New state for map markers
					setTicketStatusLoading(false);
				} catch (error) {
					console.error('Failed to fetch tickets', error);
				} finally {
					setTicketStatusLoading(false);
				}
			};

			loadTickets();

			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const isLoading = !labelCounts || !statusCounts;
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const openCount = statusCounts?.open ?? 0;
		}
	}, []);

	useEffect(() => {
		const run = async () => {
			if (!stations || stations.length === 0) {
				setLowBattery48h([]);
				setLowBatteryLoading(false);
				return;
			}

			setLowBatteryLoading(true);

			// Optionally restrict to active stations
			const targets = stations.filter((s) => s.active);

			// Batch requests to avoid hammering the API
			const chunkSize = 10;
			const out: Array<{ station: Station; mean: number }> = [];

			for (let i = 0; i < targets.length; i += chunkSize) {
				const chunk = targets.slice(i, i + chunkSize);
				const results = await Promise.all(
					chunk.map(async (s) => {
						try {
							const mean = await getBatteryValue(s.id, 'mean'); // returns number | null
							if (typeof mean === 'number' && mean <= 20) {
								return { station: s, mean };
							}
						} catch {
							// ignore errors; treat as no value
						}
						return null;
					})
				);
				results.forEach((r) => {
					if (r) out.push(r);
				});
			}

			// Sort by mean ascending
			out.sort((a, b) => a.mean - b.mean);

			setLowBattery48h(out);
			setLowBatteryLoading(false);
		};

		run();
	}, [stations]);

	// Helper function to get color class based on flagged variables
	const getVariableColorClass = (flaggedVariables: string[] = [], variable: string): string => {
		if (flaggedVariables.includes(variable)) {
			return 'text-danger'; // Red for flagged variables
		}
		return 'text-primary'; // Blue for normal variables
	};

	// Helper function for N/A styling
	const renderValueWithNA = (value: number | null | undefined, decimals: number = 0, suffix: string = ''): JSX.Element => {
		if (value === null || value === undefined) {
			return <span style={{ color: '#dc3545' }}>N/A</span>; // Red color code for N/A
		}
		return (
			<span>
				{value.toFixed(decimals)}
				{suffix}
			</span>
		);
	};

	const toHumidityPercent = (value: number | string | null | undefined): number | null | undefined => {
		if (value === null || value === undefined) return value;
		const numericValue = typeof value === 'number' ? value : Number(value);
		if (!Number.isFinite(numericValue)) return null;

		// Data contract: RH sent as ratio (<2, e.g. 0.552 or 1.003) should be converted to percent.
		const percentage = numericValue < 2 ? numericValue * 100 : numericValue;
		return Math.max(0, Math.min(100, percentage));
	};

	// Helper function to check if a variable group has any flags
	const hasVariableFlag = (flaggedVariables: string[] = [], variables: string[]): boolean => {
		return variables.some((variable) => flaggedVariables.includes(variable));
	};

	const stickyIdColumnWidth = 180;
	const stickyLastMeasurementColumnWidth = 220;

	const renderStackedHeader = (title: string, labels: string[]) => (
		<div className='home-metric-header'>
			<div className='home-metric-title'>{title}</div>
			{labels.map((label) => (
				<div key={label} className='home-metric-header-label'>
					{label}
				</div>
			))}
		</div>
	);

	const renderStackedMetricValues = (row: EngineerStationData, variableCode: string, lines: Array<{ value: number | null | undefined; decimals: number; extra?: JSX.Element }>) => {
		const flaggedVars = row.flaggedVariables || [];
		const colorClass = getVariableColorClass(flaggedVars, variableCode);

		return (
			<div className='home-metric-stack'>
				{lines.map((line, idx) => (
					<div key={`${variableCode}-${idx}`} className='home-metric-line'>
						<Link to={`/stations/${row.id}/variable/${variableCode}`} className={`${colorClass} text-decoration-none home-metric-value`}>
							{renderValueWithNA(line.value, line.decimals)}
							{line.extra}
						</Link>
					</div>
				))}
			</div>
		);
	};

	// Table configuration for engineers - simplified single-level columns
	const engineerColumns = [
		columnHelper.accessor('id', {
			header: 'Station ID',
			size: stickyIdColumnWidth,
			cell: ({ row }) => {
				const hasFlag = row.original.lastFlag;
				const lastMeasurement = row.original.lastMeasurement;

				let linkClass = 'text-success';

				// If there are flags, always show red
				if (hasFlag) {
					linkClass = 'text-danger';
				} else if (!lastMeasurement) {
					// No data at all - red
					linkClass = 'text-danger';
				} else {
					// Check communication delay
					const measurementMoment = moment.utc(lastMeasurement);
					const nowMoment = moment.utc();
					const hoursDiff = nowMoment.diff(measurementMoment, 'hours', true);

					if (hoursDiff >= 18) {
						linkClass = 'text-danger';
					} else if (hoursDiff >= 12) {
						linkClass = 'text-warning';
					}
					// else stays green (success)
				}

				return (
					<div>
						<button type='button' className={`${linkClass} text-decoration-none btn btn-link p-0`} onClick={() => openStationModal(row.original)} title='View station details'>
							{row.original.id}
						</button>
						<div className='text-muted small text-truncate' style={{ maxWidth: `${stickyIdColumnWidth - 10}px` }} title={row.original.name}>
							{row.original.name}
						</div>
					</div>
				);
			},
			meta: {
				sticky: 'left',
			},
		}),

		columnHelper.accessor('lastMeasurement', {
			header: 'Last Measurement',
			size: stickyLastMeasurementColumnWidth,
			cell: ({ row }) => {
				const lastMeasurement = row.original.lastMeasurement;
				if (!lastMeasurement) return '-';

				const measurementMoment = moment.utc(lastMeasurement);
				const nowMoment = moment.utc();
				const hoursDiff = nowMoment.diff(measurementMoment, 'hours', true);

				let colorClass = 'success';
				if (hoursDiff >= 18) {
					colorClass = 'danger';
				} else if (hoursDiff >= 12) {
					colorClass = 'warning';
				}

				return (
					<div>
						<div style={{ whiteSpace: 'nowrap' }}>{measurementMoment.local().format('MM/DD/YY HH:mm')}</div>
						<small className={`text-${colorClass}`}>({Math.round(hoursDiff)}h ago)</small>
					</div>
				);
			},
			meta: {
				sticky: 'left',
			},
		}),

		columnHelper.accessor('minimumBattery', {
			header: () => renderStackedHeader('Battery (%)', ['Min', 'Max']),
			size: 120,
			cell: ({ row }) =>
				renderStackedMetricValues(row.original, 'lb', [
					{ value: row.original.minimumBattery, decimals: 0 },
					{ value: row.original.maximumBattery, decimals: 0 },
				]),
		}),

		columnHelper.accessor('voltageMin', {
			header: () => renderStackedHeader('Voltage (V)', ['Min', 'Max']),
			size: 120,
			cell: ({ row }) =>
				renderStackedMetricValues(row.original, 'lv', [
					{ value: row.original.voltageMin, decimals: 0 },
					{ value: row.original.voltageMax, decimals: 0 },
				]),
		}),

		columnHelper.accessor('temperatureMin', {
			header: () => renderStackedHeader('Temperature (°C)', ['Min', 'Max']),
			size: 120,
			cell: ({ row }) =>
				renderStackedMetricValues(row.original, 'te', [
					{ value: row.original.temperatureMin, decimals: 1 },
					{ value: row.original.temperatureMax, decimals: 1 },
				]),
		}),

		columnHelper.accessor('windAvg', {
			header: () => renderStackedHeader('Wind (m/s)', ['Avg', 'Max']),
			size: 120,
			cell: ({ row }) =>
				renderStackedMetricValues(row.original, 'wg', [
					{ value: row.original.windAvg, decimals: 1 },
					{ value: row.original.windMax, decimals: 1 },
				]),
		}),

		columnHelper.accessor('radiationAvg', {
			header: () => renderStackedHeader('Radiation (W/m²)', ['Avg', 'Max']),
			size: 120,
			cell: ({ row }) =>
				renderStackedMetricValues(row.original, 'ra', [
					{ value: row.original.radiationAvg, decimals: 0 },
					{ value: row.original.radiationMax, decimals: 0 },
				]),
		}),

		columnHelper.accessor('precipitationSum', {
			header: () => renderStackedHeader('Precipitation (mm)', ['Sum']),
			size: 120,
			cell: ({ row }) => {
				const hasMultipleSensors = row.original.hasMultiplePrecipitationSensors;

				return renderStackedMetricValues(row.original, 'pr', [
					{
						value: row.original.precipitationSum,
						decimals: 2,
						extra: hasMultipleSensors ? (
							<span className='ms-1' title='Multiple precipitation sensors detected'>
								<i className='fa fa-clone text-info' style={{ fontSize: '0.75em' }}></i>
							</span>
						) : undefined,
					},
				]);
			},
		}),

		columnHelper.accessor('humidityMin', {
			header: () => renderStackedHeader('Relative Humidity (%)', ['Min', 'Max']),
			size: 130,
			cell: ({ row }) =>
				renderStackedMetricValues(row.original, 'rh', [
					{ value: toHumidityPercent(row.original.humidityMin), decimals: 0 },
					{ value: toHumidityPercent(row.original.humidityMax), decimals: 0 },
				]),
		}),

		columnHelper.accessor('pressureMin', {
			header: () => renderStackedHeader('Pressure (kPa)', ['Min', 'Max']),
			size: 120,
			cell: ({ row }) =>
				renderStackedMetricValues(row.original, 'ap', [
					{ value: row.original.pressureMin, decimals: 0 },
					{ value: row.original.pressureMax, decimals: 0 },
				]),
		}),
	];

	const table = useReactTable({
		data: engineerStationData,
		columns: engineerColumns,
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		initialState: {
			pagination: {
				pageSize: 20,
			},
		},
		state: {
			globalFilter,
			sorting,
			columnPinning: {
				left: ['id', 'lastMeasurement'],
			},
		},
		onGlobalFilterChange: setGlobalFilter,
		onSortingChange: setSorting,
	});

	const options: ApexCharts.ApexOptions = {
		chart: {
			type: 'donut',
			events: {
				dataPointSelection: (event, chartContext, config) => {
					const selectedLabel = Object.keys(labelCounts!)[config.dataPointIndex];
					navigate(`/tickets/label/${selectedLabel}`);
				},
			},
		},
		labels: Object.keys(labelCounts || {}),
		legend: { position: 'bottom' },
		responsive: [
			{
				breakpoint: 480,
				options: {
					chart: { width: 300 },
					legend: { position: 'bottom' },
				},
			},
		],
	};

	const ticketChartOptions: ApexCharts.ApexOptions = {
		chart: {
			type: 'pie',
			events: {
				dataPointSelection: (event, chartContext, config) => {
					const selectedStatus = Object.keys(statusCounts!)[config.dataPointIndex];
					navigate(`/tickets/status/${selectedStatus}`);
				},
			},
		},
		labels: Object.keys(statusCounts || {}),
		legend: { position: 'bottom' },
		colors: ['#3b82f6', '#facc15', '#10b981'],
		responsive: [
			{
				breakpoint: 480,
				options: {
					chart: { width: 300 },
					legend: { position: 'bottom' },
				},
			},
		],
	};

	const openCount = statusCounts?.open ?? 0;

	// Prepare data for the Apex bar chart
	const countryNames = Object.keys(stationsByCountry);
	const stationCounts = countryNames.map((name) => stationsByCountry[name].count);

	const countryChartOptions: ApexCharts.ApexOptions = {
		chart: {
			type: 'bar' as const,
			events: {
				dataPointSelection: (event, chartContext, config) => {
					const index = config.dataPointIndex;
					const selectedCountryName = countryNames[index];
					const selectedCountryCode = stationsByCountry[selectedCountryName]?.code;
					if (selectedCountryCode) navigate(`/stations/country/${selectedCountryCode}`);
				},
			},
		},
		xaxis: { categories: countryNames },
	};

	const countryChartSeries = [{ name: 'Stations', data: stationCounts }];

	const activityChartOptions: ApexCharts.ApexOptions = {
		chart: {
			type: 'pie',
			events: {
				dataPointSelection: (event, chartContext, config) => {
					const statusMapping = ['active', 'inactive'];
					const selectedStatus = statusMapping[config.dataPointIndex];
					navigate(`/stations/status/${selectedStatus}`);
				},
			},
		},
		labels: ['Active', 'Inactive'],
		colors: ['#10b981', '#ef4444'],
		legend: { position: 'bottom' },
		responsive: [
			{
				breakpoint: 480,
				options: {
					chart: { width: 200 },
					legend: { position: 'bottom' },
				},
			},
		],
	};

	// Handle marker click to update the selected location
	const handleMarkerClick = (ticket: Ticket) => {
		navigate(`stations/${ticket.station}`);
	};

	// Function to generate a custom icon based on the status of the location
	const getMarkerIcon = (status: string): L.DivIcon => {
		let markerColor: string = 'green';
		let iconClass: string = 'fa-check-circle';

		switch (status) {
			case 'danger':
				markerColor = 'red';
				iconClass = 'fa-map-marker';
				break;
			case 'caution':
				markerColor = 'yellow';
				iconClass = 'fa-map-marker';
				break;
			case 'safe':
				markerColor = 'green';
				iconClass = 'fa-map-marker';
				break;
			default:
				markerColor = 'blue';
				iconClass = 'fa-map-marker';
		}

		return L.divIcon({
			className: 'custom-icon',
			html: `<div class="fa ${iconClass}" style="font-size: 36px; color: ${markerColor};"></div>`,
			iconSize: [30, 30],
			iconAnchor: [15, 30],
		});
	};

	const getMarkerStatus = (status: string) => {
		switch (status) {
			case 'open':
				return 'danger';
			case 'pending':
				return 'caution';
			case 'closed':
				return 'safe';
			default:
				return 'unknown';
		}
	};

	// No Role View - Contact Admin
	if (!hasRole) {
		return (
			<div className='content-page'>
				<div className='content'>
					<div className='container-fluid'>
						<div className='row justify-content-center'>
							<div className='col-xl-6 col-lg-8'>
								<div className='card mt-5'>
									<div className='card-body text-center p-5'>
										<div className='mb-4'>
											<i className='fa fa-user-times fa-4x text-warning'></i>
										</div>
										<h3 className='card-title'>Access Restricted</h3>
										<p className='card-text mb-4'>Your account does not have a role assigned. Please contact your system administrator to get access permissions.</p>
										<div className='alert alert-info' role='alert'>
											<strong>Need help?</strong> Contact your administrator to assign you a role (Engineer, Network Manager, or Country Manager) to access the dashboard.
										</div>
										<div className='mt-4'>
											<button className='btn btn-primary me-2' onClick={() => (window.location.href = 'mailto:admin@yourcompany.com?subject=Dashboard Access Request')}>
												<i className='fa fa-envelope me-2'></i>Contact Admin
											</button>
											<button className='btn btn-secondary' onClick={() => window.location.reload()}>
												<i className='fa fa-refresh me-2'></i>Refresh
											</button>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (isEngineer || isCountryManager) {
		return (
			<div className='content-page'>
				<div className='content'>
					<div className='container-fluid'>
						<div className='row'>
							<div className='col-12'>
								<div className='card'>
									<div className='card-header'>
										<div className='row align-items-center'>
											<div className='col'>
												<h4 className='card-title mb-0'>Station Technical Overview</h4>
												<small className='text-muted'>
													Showing metrics for the last {timeRange} hours
												</small>
												{expandedDataLoading && (
													<small className='text-muted ms-2'>
														<i className='fa fa-spinner fa-spin me-1'></i>
														Loading detailed metrics...
													</small>
												)}
											</div>
											<div className='col-auto'>
												<div className='d-flex align-items-center'>
													<select
														className='form-select form-select-sm me-2'
														value={timeRange}
														onChange={(e) => setTimeRange(Number(e.target.value))}
														style={{ width: 'auto' }}
													>
														<option value={12}>Last 12 hours</option>
														<option value={24}>Last 24 hours</option>
														<option value={48}>Last 48 hours</option>
													</select>
													<input
														type='text'
														className='form-control me-2'
														placeholder='Search stations...'
														value={globalFilter ?? ''}
														onChange={(e) => setGlobalFilter(String(e.target.value))}
														style={{ width: '200px' }}
													/>
													<span className='badge bg-primary'>{engineerStationData.length} stations</span>
												</div>
											</div>
										</div>
									</div>
									<div className='card-body'>
										{engineerDataLoading ? (
											<div className='text-center py-5'>
												<div className='spinner-border text-primary' role='status'>
													<span className='visually-hidden'>Loading station data...</span>
												</div>
												<p className='mt-2 text-muted'>Loading station metrics...</p>
											</div>
										) : hasError ? (
											<div className='alert alert-danger'>
												<div className='d-flex justify-content-between align-items-center'>
													<span>
														<i className='fa fa-exclamation-triangle me-2'></i>
														Failed to load station data: {(metricsError as Error)?.message || 'Unknown error'}
													</span>
													<button className='btn btn-sm btn-outline-danger' onClick={handleManualRefresh} disabled={isManualRefreshing}>
														<i className={`fa fa-refresh ${isManualRefreshing ? 'fa-spin' : ''} me-1`}></i>
														Retry
													</button>
												</div>
											</div>
										) : (
											<>
												{/* Add refresh button in header */}
												<div className='d-flex justify-content-between align-items-center mb-2'>
													<small className='text-muted'>
														{engineerDataLoading || isManualRefreshing ? (
															<>
																<i className='fa fa-spinner fa-spin me-1'></i>
																Fetching data...
															</>
														) : (
															<>Last updated: {new Date().toLocaleTimeString()}</>
														)}
													</small>
													<button
														className={`btn btn-sm ${isManualRefreshing ? 'btn-secondary' : 'btn-outline-primary'}`}
														onClick={handleManualRefresh}
														disabled={isManualRefreshing || engineerDataLoading}
														title='Refresh data'>
														<i className={`fa fa-refresh ${isManualRefreshing ? 'fa-spin' : ''}`}></i>
														{isManualRefreshing && <span className='ms-1'>Refreshing...</span>}
													</button>
												</div>

												{/* Show loading bar when background task is running */}
												{(engineerDataLoading || isManualRefreshing || engineerStationData.length === 0) && !hasError && (
													<div className='progress mb-3' style={{ height: '3px' }}>
														<div className='progress-bar progress-bar-striped progress-bar-animated bg-primary' role='progressbar' style={{ width: '100%' }}></div>
													</div>
												)}

												{/* Show message when data is being fetched in background */}
												{engineerStationData.length === 0 && !engineerDataLoading && !hasError && (
													<div className='alert alert-info d-flex align-items-center'>
														<i className='fa fa-info-circle me-2'></i>
														<div>
															<strong>Loading station metrics...</strong>
															<p className='mb-0 small'>Data is being fetched in the background. This may take a moment for large datasets.</p>
														</div>
													</div>
												)}

												{/* Warning for failed stations */}
												{(() => {
													const failed = (engineerStationData || []).filter((s: EngineerStationData) => s.fetchFailed) as EngineerStationData[];
													if (!failed.length) return null;
													const maxShow = 20;
													const shown = failed
														.slice(0, maxShow)
														.map((s: EngineerStationData) => s.id)
														.join(', ');
													const extra = failed.length > maxShow ? ` and ${failed.length - maxShow} more` : '';
													return (
														<div className='alert alert-warning d-flex justify-content-between align-items-start'>
															<div>
																<strong>Some stations failed to fetch metrics.</strong>
																<div className='small mt-1'>
																	Failed stations ({failed.length}): {shown}
																	{extra}
																</div>
															</div>
															<i className='fa fa-exclamation-triangle text-warning'></i>
														</div>
													);
												})()}

												<div className='table-responsive'>
													<table className='table table-hover table-sm'>
														<thead className='table-light'>
															{table.getHeaderGroups().map((headerGroup) => (
																<tr key={headerGroup.id}>
																	{headerGroup.headers.map((header) => {
																	const isSticky = header.column.getIsPinned() === 'left';
																	const stickyOffset = header.column.getStart('left');
																	const headerTop = headerGroup.depth * 52;

																	return (
																		<th
																			key={header.id}
																			colSpan={header.colSpan}
																			className={`${header.column.getCanSort() ? 'cursor-pointer' : ''} ${isSticky ? 'sticky-left-header-cell' : ''}`.trim()}
																			onClick={header.column.getToggleSortingHandler()}
																			style={{
																					minWidth: header.getSize(),
																					width: header.getSize(),
																					maxWidth: header.getSize(),
																					textAlign: 'center',
																					position: 'sticky',
																					top: `${headerTop}px`,
																					backgroundColor: '#f8f9fa',
																					zIndex: isSticky ? 60 - headerGroup.depth : 30 - headerGroup.depth,
																					overflow: 'hidden',
																					...(isSticky ? {
																						left: `${stickyOffset}px`,
																					} : {}),
																				}}>
																			{header.isPlaceholder ? null : (
																				<div className='d-flex align-items-center justify-content-center'>
																					{flexRender(header.column.columnDef.header, header.getContext())}
																					{header.column.getCanSort() && (
																						<i
																							className={`fa ms-1 ${
																								header.column.getIsSorted() === 'asc'
																									? 'fa-sort-up'
																									: header.column.getIsSorted() === 'desc'
																									? 'fa-sort-down'
																									: 'fa-sort'
																							}`}></i>
																					)}
																				</div>
																			)}
																		</th>
																	);
																})}
															</tr>
														))}
													</thead>
													<tbody>
														{table.getRowModel().rows.map((row) => (
															<tr key={row.id} onMouseEnter={() => handleRowMouseEnter(row.original.id)}>
																{row.getVisibleCells().map((cell) => {
																	const isSticky = cell.column.getIsPinned() === 'left';
																	const stickyOffset = cell.column.getStart('left');

																	return (
																		<td
																			key={cell.id}
																			className={isSticky ? 'sticky-left-body-cell' : ''}
																			style={{
																					minWidth: cell.column.getSize(),
																					width: cell.column.getSize(),
																					maxWidth: cell.column.getSize(),
																					position: isSticky ? 'sticky' : 'relative',
																					zIndex: isSticky ? 5 : 1,
																				...(isSticky ? {
																					left: `${stickyOffset}px`,
																					backgroundColor: '#fff',
																					overflow: 'hidden',
																				} : {}),
																			}}
																		>
																			{flexRender(cell.column.columnDef.cell, cell.getContext())}
																		</td>
																	);
																})}
															</tr>
														))}
													</tbody>
												</table>
											</div>

											{/* Pagination */}
											<div className='d-flex justify-content-between align-items-center mt-3'>
													<div className='d-flex align-items-center'>
														<span className='text-muted me-3'>
															Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
															{Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, table.getFilteredRowModel().rows.length)} of{' '}
															{table.getFilteredRowModel().rows.length} stations
														</span>
														<select
															className='form-select form-select-sm'
															value={table.getState().pagination.pageSize}
															onChange={(e) => table.setPageSize(Number(e.target.value))}
															style={{ width: 'auto' }}>
															{[10, 20, 30, 50, 100].map((pageSize) => (
																<option key={pageSize} value={pageSize}>
																	Show {pageSize}
																</option>
															))}
														</select>
													</div>
													<nav>
														<ul className='pagination pagination-sm mb-0'>
															<li className={`page-item ${!table.getCanPreviousPage() ? 'disabled' : ''}`}>
																<button className='page-link' onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>
																	First
																</button>
															</li>
															<li className={`page-item ${!table.getCanPreviousPage() ? 'disabled' : ''}`}>
																<button className='page-link' onClick={table.previousPage} disabled={!table.getCanPreviousPage()}>
																	Previous
																</button>
															</li>
															<li className='page-item active'>
																<span className='page-link'>
																	{table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
																</span>
															</li>
															<li className={`page-item ${!table.getCanNextPage() ? 'disabled' : ''}`}>
																<button className='page-link' onClick={table.nextPage} disabled={!table.getCanNextPage()}>
																	Next
																</button>
															</li>
															<li className={`page-item ${!table.getCanNextPage() ? 'disabled' : ''}`}>
																<button className='page-link' onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()}>
																	Last
																</button>
															</li>
														</ul>
													</nav>
												</div>
											</>
										)}
									</div>
								</div>
							</div>
						</div>

						{isStationModalOpen && (
							<>
								<div className='modal fade show' style={{ display: 'block' }} role='dialog' aria-modal='true'>
									<div className='modal-dialog modal-lg modal-dialog-centered'>
										<div className='modal-content'>
											<div className='modal-header'>
												<h5 className='modal-title'>Station Details</h5>
												<button type='button' className='btn-close' onClick={closeStationModal} aria-label='Close'></button>
											</div>

											<div className='modal-body station-details-modal'>
												<h6 className='mb-1'>{selectedStationInfo?.name || selectedStationInfo?.id}</h6>
												<p className='text-muted mb-1'>ID: {selectedStationInfo?.id}</p>
												{(() => {
													const code = selectedStationInfo?.countryCode || null;
													if (!code) return null;
													const name = COUNTRY_NAMES[code] || code;
													return (
														<p className='text-muted mb-3'>
															Country: {name} <span className='ms-1'>({code})</span>
														</p>
													);
												})()}

												{/* Communication Problem Warning */}
												{(() => {
													const lastMeasurement = selectedStationInfo?.lastMeasurement;
													if (!lastMeasurement) {
														return (
															<div className='alert alert-danger d-flex align-items-center mb-3' role='alert'>
																<i className='fa fa-exclamation-triangle me-2'></i>
																<div>
																	<strong>Communication Problem:</strong> No data received from station
																</div>
															</div>
														);
													}

													const measurementMoment = moment.utc(lastMeasurement);
													const nowMoment = moment.utc();
													const hoursDiff = nowMoment.diff(measurementMoment, 'hours', true);

													if (hoursDiff >= 18) {
														return (
															<div className='alert alert-danger d-flex align-items-center mb-3' role='alert'>
																<i className='fa fa-exclamation-triangle me-2'></i>
																<div>
																	<strong>Communication Problem:</strong> No data for {Math.round(hoursDiff)} hours
																</div>
															</div>
														);
													} else if (hoursDiff >= 12) {
														return (
															<div className='alert alert-warning d-flex align-items-center mb-3' role='alert'>
																<i className='fa fa-exclamation-triangle me-2'></i>
																<div>
																	<strong>Communication Warning:</strong> No data for {Math.round(hoursDiff)} hours
																</div>
															</div>
														);
													}
													return null;
												})()}

												{isStationDetailsLoading ? (
													<div className='d-flex flex-column align-items-center justify-content-center py-4'>
														<div className='spinner-border text-primary' role='status'>
															<span className='visually-hidden'>Loading station details...</span>
														</div>
														<div className='mt-2 text-muted'>Loading station details...</div>
													</div>
												) : (
													<div className='row'>
														{/* Map Section */}
														<div className='col-md-6 mb-3'>
															<div className='row'>
																<div
																	ref={modalMapContainerRef}
																	style={{
																		height: '250px',
																		borderRadius: '4px',
																		overflow: 'hidden',
																		border: '1px solid #eee',
																	}}
																/>
																{selectedStationInfo && (selectedStationInfo.lat == null || selectedStationInfo.lng == null) && (
																	<div className='text-muted small mt-2'>Location unavailable</div>
																)}
															</div>
															<div className='row'>
																{/* Tilt Visualization Section */}
																<div className='col-12 mt-4'>
																	<h6 className='mb-2'>Tilt </h6>
																	{tiltsLoading ? (
																		<div className='d-flex flex-column align-items-center justify-content-center py-3'>
																			<div className='spinner-border text-primary' role='status'>
																				<span className='visually-hidden'>Loading tilts...</span>
																			</div>
																			<div className='mt-2 text-muted'>Loading tilts...</div>
																		</div>
																	) : tilts && tilts.length > 0 ? (
																		<div className='d-flex justify-content-center'>
																			<TiltCanvas tilts={tilts} />
																		</div>
																	) : (
																		<div className='text-muted'>No tilt data available</div>
																	)}
																</div>
															</div>
														</div>

														{/* Flagged Variables Section */}
														<div className='col-md-6'>
															<div className='d-flex align-items-center justify-content-between'>
																<h6 className='mb-0'>Flagged variables</h6>
																{isFlagsLoading && (
																	<small className='text-muted ms-2'>
																		<i className='fa fa-spinner fa-spin me-1'></i>
																		Filtering...
																	</small>
																)}
															</div>
															{selectedStationInfo?.flaggedVariables && selectedStationInfo.flaggedVariables.length > 0 ? (
																<ul className='mb-0'>
																	{selectedStationInfo.flaggedVariables.map((v: string) => {
																		const name = variableNameMap[v] || v;
																		const count = selectedStationInfo?.flaggedVariablesDetail?.[v]?.count;
																		const countText = typeof count === 'number' ? `${count} measurements flagged` : null;
																		return (
																			<li key={v}>
																				<Link
																					to={`/stations/${selectedStationInfo?.id}/variable/${v}`}
																					className='text-decoration-none'
																					onClick={closeStationModal}>
																					{name}
																					{name !== v && <span className='text-muted ms-1'>({v})</span>}
																				</Link>
																				{countText && <span className='text-muted ms-2'>— {countText}</span>}
																				{(() => {
																					const details = selectedStationInfo?.flaggedVariablesDetail?.[v] || {};
																					const entries = Object.entries(details).filter(([k, val]) => {
																						if (k === 'count') return false;
																						if (val === null || val === undefined || val === false) return false;
																						if (val === 1 || val === '1') return false;
																						return true;
																					});
																					if (entries.length === 0) return null;
																					return (
																						<ul className='small text-muted mt-1 mb-0'>
																							{entries.map(([k, val]) => {
																								const label = QC_ACRONYM_MAP[k] || k;
																								let statusText: string;
																								if (typeof val === 'number') {
																									statusText = QC_SEVERITY_MAP[val as number] || `status ${val}`;
																								} else if (val === true) {
																									statusText = 'triggered';
																								} else if (typeof val === 'string') {
																									statusText = val;
																								} else {
																									try {
																										statusText = JSON.stringify(val);
																									} catch (e) {
																										statusText = String(val);
																									}
																								}
																								const explanation = QC_EXPLANATION_MAP[k];
																								const isKnownSeverity =
																									statusText === 'erroneous' || statusText === 'doubtful' || statusText === 'inconsistent';
																								let displayText: string;
																								if (explanation && isKnownSeverity) {
																									displayText = `${label} (${explanation}) is ${statusText}`;
																								} else if (explanation && statusText === 'triggered') {
																									displayText = `${label} (${explanation}) was triggered`;
																								} else {
																									displayText = `${label}: ${statusText}`;
																								}
																								return <li key={k}>{displayText}</li>;
																							})}
																						</ul>
																					);
																				})()}
																			</li>
																		);
																	})}
																</ul>
															) : (
																<div className='text-muted'>No flagged variables</div>
															)}
														</div>
													</div>
												)}
											</div>

											<div className='modal-footer'>
												<button className='btn btn-secondary' onClick={closeStationModal}>
													Close
												</button>
											</div>
										</div>
									</div>
								</div>
								<div className='modal-backdrop fade show'></div>
							</>
						)}
					</div>
				</div>
			</div>
		);
	}

	if (isNetworkManager) {
		return (
			<div className='content-page'>
				<div className='content'>
					<div className='container-fluid'>
						<div className='row'>
							<div className='col-12'>
								<div className='page-title-box'>
									<h4 className='page-title'>Home</h4>
								</div>
							</div>
						</div>

						<div className='row'>
							<div className='col-xl-7'>
								<div className='row'>
									<div className='col'>
										<div className='card tilebox-one'>
											<div className='card-body'>
												<h6 className='text-uppercase mt-0'>Stations</h6>
												{flagError && <p className='text-red-600'>Error: {flagError}</p>}
												{stationLoading ? (
													<p>Loading station data...</p>
												) : (
													<div>
														<h2 className='my-2' id='active-users-count'>
															{activeCount}
														</h2>
														<p className='mb-0 text-muted'>
															<Link to='/stations' className='text-nowrap'>
																View
															</Link>
														</p>
													</div>
												)}
											</div>
										</div>
									</div>

									<div className='col'>
										<div className='card tilebox-one'>
											<div className='card-body'>
												<h6 className='text-uppercase mt-0'>Flagged Stations</h6>
												{metricsError && <p className='text-red-600'>Error: {flagError}</p>}
												{stationLoading ? (
													<p>Loading flagged station data...</p>
												) : (
													<div>
														<h2 className='my-2' id='active-users-count'>
															{flaggedCount}
														</h2>
														<p className='mb-0 text-muted'>
															<button onClick={scrollToStationIssues} className='btn btn-link p-0 text-nowrap' style={{ textDecoration: 'none' }}>
																View
															</button>
														</p>
													</div>
												)}
											</div>
										</div>
									</div>

									<div className='col'>
										<div className='card tilebox-one'>
											<div className='card-body'>
												<h6 className='text-uppercase mt-0'>Open Tickets</h6>
												{metricsError && <p className='text-red-600'>Error: {ticketError}</p>}
												{ticketStatusLoading ? (
													<p>Loading ticket count data...</p>
												) : (
													<div>
														<h2 className='my-2' id='active-users-count'>
															{openCount}
														</h2>
														<p className='mb-0 text-muted'>
															<Link to='/tickets/status/open' className='text-nowrap'>
																View
															</Link>
														</p>
													</div>
												)}
											</div>
										</div>
									</div>
								</div>

								{/* Stations by country — HIDE for engineers */}
								{!isEngineer && (
									<div className='row'>
										<div className='col-xl-12'>
											<div className='card'>
												<div className='d-flex card-header justify-content-between align-items-center'>
													<h4 className='header-title'>Stations by country</h4>
												</div>
												<div className='card-body'>
													<Chart options={countryChartOptions} series={countryChartSeries} type='bar' height={400} />
												</div>
											</div>
										</div>
									</div>
								)}
							</div>

							<div className='col-xl-5'>
								<div className={`card bg-danger`}>
									<div className='card-body'>
										<div className='d-flex align-items-center'>
											<div className='flex-shrink-0'>
												<i className='fa fa-battery-1 fa-2x text-white'></i>
											</div>
											<div className='flex-grow-1 ms-3'>
												<h4 className='mt-0 mb-1 text-white'>Stations with Battery 20% and Below</h4>
												<small className='text-white-50'>{stationLoading || lowBatteryLoading ? 'Computing…' : `${lowBattery48h.length} station(s)`}</small>
											</div>
										</div>
									</div>
									<div className='card-body bg-white' style={{ borderTopLeftRadius: 8, borderTopRightRadius: 8 }}>
										{stationLoading || lowBatteryLoading ? (
											<p className='mb-0'>Loading station battery (48h mean)…</p>
										) : lowBattery48h.length === 0 ? (
											<p className='mb-0'>No stations at or below 20% (48h mean).</p>
										) : (
											<div className='table-responsive' style={{ maxHeight: 550, overflowY: 'auto' }}>
												<table className='table table-sm align-middle mb-0'>
													<thead
														style={{
															position: 'sticky',
															top: 0,
															zIndex: 1,
															background: '#fff',
															boxShadow: '0 1px 0 rgba(0,0,0,0.05)',
														}}>
														<tr>
															<th style={{ whiteSpace: 'nowrap' }}>Station ID</th>
															<th>Name</th>
															<th className='text-end' style={{ whiteSpace: 'nowrap' }}>
																48h Mean
															</th>
														</tr>
													</thead>
													<tbody>
														{lowBattery48h.map(({ station, mean }) => (
															<tr key={station.id}>
																<td style={{ whiteSpace: 'nowrap' }}>
																	<Link to={`/stations/${station.id}/timeseries/battery`} className='text-primary text-decoration-none'>
																		<code>{station.id}</code>
																	</Link>
																</td>
																<td>{station.name || '-'}</td>
																<td className='text-end'>
																	<span className='badge' style={{ backgroundColor: '#dc2626', color: '#fff' }}>
																		{Math.round(mean)}%
																	</span>
																</td>
															</tr>
														))}
													</tbody>
												</table>
											</div>
										)}
									</div>
								</div>

								{/*    <MapContainer center={[0, 20]} zoom={3} scrollWheelZoom={true}>*/}
								{/*        <TileLayer*/}
								{/*            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"*/}
								{/*            attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'*/}
								{/*        />*/}
								{/*        {ticketsWithLocation.map((ticket) => {*/}
								{/*            if (ticket.lat && ticket.lng) {*/}
								{/*                return (*/}
								{/*                    <Marker*/}
								{/*                        key={ticket._id}*/}
								{/*                        position={[ticket.lat, ticket.lng]}*/}
								{/*                        icon={getMarkerIcon(getMarkerStatus(ticket.status))}*/}
								{/*                        eventHandlers={{click: () => handleMarkerClick(ticket)}}*/}
								{/*                    >*/}
								{/*                        <Popup>*/}
								{/*                            <strong>{ticket.title}</strong><br/>*/}
								{/*                            Status: {ticket.status}<br/>*/}
								{/*                            Station: {ticket.station}*/}
								{/*                        </Popup>*/}
								{/*                    </Marker>*/}
								{/*                );*/}
								{/*            }*/}
								{/*            return null;*/}
								{/*        })}*/}
								{/*    </MapContainer>*/}
							</div>
						</div>

						<div className='row' ref={stationIssuesRef}>
							<div className='col-12'>
								<StationIssuesCard />
							</div>
						</div>

						{/* Bottom 3 cards — HIDE for engineers */}
						{!isEngineer && (
							<div className='row'>
								<div className='col-xl-4 col-lg-6'>
									<div className='card'>
										<div className='d-flex card-header justify-content-between align-items-center'>
											<h4 className='header-title'>Most Common Issues</h4>
										</div>
										<div className='card-body pt-0'>
											{ticketStatusLoading ? <p>Loading...</p> : <ReactApexChart options={options} series={Object.values(labelCounts!)} type='donut' width={400} />}
										</div>
									</div>
								</div>

								<div className='col-xl-4 col-lg-6'>
									<div className='card'>
										<div className='d-flex card-header justify-content-between align-items-center'>
											<h4 className='header-title'>Ticket By Status</h4>
										</div>
										<div className='card-body pt-0'>
											{ticketStatusLoading ? <p>Loading...</p> : <Chart options={ticketChartOptions} series={Object.values(statusCounts!)} type='pie' width={400} />}
										</div>
									</div>
								</div>

								<div className='col-xl-4 col-lg-6'>
									<div className='card'>
										<div className='d-flex card-header justify-content-between align-items-center'>
											<h4 className='header-title'>Station Status</h4>
										</div>
										<div className='card-body pt-0'>
											{stationLoading ? <p>Loading...</p> : <Chart options={activityChartOptions} series={stationActivityData} type='pie' width={400} />}
										</div>
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		);
	}

	return null;
};
export default Home;
